import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  CreditCard,
  Download,
  FileText,
  Loader2,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldAlert,
  SlidersHorizontal,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useCompany } from "@/hooks/useCompany";
import { TRIAL_READ_ONLY_MESSAGE, useTrialMode } from "@/hooks/useTrialMode";
import { db, money } from "@/lib/billing";
import { getCreditPacks } from "@/lib/credits/api";

type CompanyRow = { id: string; name: string; slug: string | null };
type WalletRow = { company_id: string; balance: number; lifetime_granted: number; lifetime_consumed: number; updated_at: string };
type SubscriptionRow = { company_id: string | null; package_id: string | null; status: string; current_period_end: string | null; created_at: string };
type PackageFeatureRow = { package_id: string; feature_value: { amount?: number } | number | null };
type UsageRow = {
  id: string;
  company_id: string | null;
  website_id: string | null;
  article_id: string | null;
  task_type: string;
  model_key: string | null;
  credits_charged: number;
  status: string;
  created_at: string;
};
type TransactionRow = {
  id: string;
  company_id: string;
  website_id: string | null;
  article_id: string | null;
  job_id: string | null;
  user_id: string | null;
  delta: number;
  reason: string;
  idempotency_key: string | null;
  metadata: Record<string, unknown> | null;
  balance_before: number | null;
  balance_after: number;
  feature_key: string | null;
  action: string | null;
  credit_type: string | null;
  related_entity_type: string | null;
  related_entity_id: string | null;
  status: "success" | "failed" | "reversed";
  error_details: string | null;
  created_at: string;
};
type FeatureSettingRow = {
  id: string;
  company_id: string;
  feature_key: string;
  feature_name: string;
  description: string | null;
  credit_type: string;
  enabled: boolean;
  require_confirmation: boolean;
  monthly_credit_limit: number | null;
  daily_credit_limit: number | null;
  allow_admin_override: boolean;
  block_when_limit_reached: boolean;
  insufficient_credits_message: string;
  updated_at: string;
};
type ModelRow = { task_type: string; credits_cost: number; label: string };
type WebsiteRow = { id: string; company_id: string | null; name: string; domain: string | null };
type ArticleRow = { id: string; company_id: string | null; website_id: string | null; title: string | null };
type ProfileRow = { id: string; full_name: string | null };

type DashboardData = {
  companies: CompanyRow[];
  wallets: WalletRow[];
  subscriptions: SubscriptionRow[];
  packageFeatures: PackageFeatureRow[];
  usage: UsageRow[];
  transactions: TransactionRow[];
  settings: FeatureSettingRow[];
  models: ModelRow[];
  websites: WebsiteRow[];
  articles: ArticleRow[];
  profiles: ProfileRow[];
};

const FEATURE_CATALOG = [
  { key: "rewrite", task: "rewrite", name: "AI article generation", description: "Creates or rewrites article content through the configured AI model." },
  { key: "translate", task: "translate", name: "AI translation", description: "Translates article content while preserving editorial structure." },
  { key: "seo", task: "seo", name: "AI title/meta generation", description: "Generates SEO titles, summaries, metadata, tags, or scoring text." },
  { key: "image", task: "image", name: "AI image generation", description: "Creates images through AI image providers." },
  { key: "smart_publish_rewrite", task: "rewrite", name: "Smart publish with AI rewrite", description: "Generates publication-ready articles during smart publish jobs." },
  { key: "auto_publish_rewrite", task: "rewrite", name: "Auto-publish jobs using AI", description: "Rewrites imported news during scheduled or manual auto-publish runs." },
  { key: "media_match", task: null, name: "Media/image matching", description: "Matches existing media assets to articles; billed only when a credit debit is recorded." },
  { key: "qa", task: "qa", name: "Editorial QA pass", description: "Runs AI quality, compliance, and editorial review checks." },
];

const CHART_COLORS = ["#2563eb", "#16a34a", "#f97316", "#dc2626", "#7c3aed", "#0891b2", "#ca8a04", "#64748b"];

const startOfMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

function scopeQuery<T>(query: T, companyId: string | null, isPlatformOwner: boolean): T {
  if (!isPlatformOwner && companyId && typeof (query as { eq?: unknown }).eq === "function") {
    return (query as { eq: (column: string, value: string) => T }).eq("company_id", companyId);
  }
  return query;
}

function featureFromTransaction(tx: TransactionRow) {
  const meta = tx.metadata ?? {};
  return tx.feature_key || String(meta.feature_key || meta.task_type || tx.reason);
}

function monthKey(date: string) {
  return new Date(date).toLocaleDateString(undefined, { month: "short", year: "2-digit" });
}

function formatDateTime(value?: string | null) {
  if (!value) return "Never";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function numericInputValue(value: number | null) {
  return value === null || Number.isNaN(value) ? "" : String(value);
}

function isMissingSchemaError(error: unknown) {
  const text = error && typeof error === "object" && "message" in error ? String((error as { message?: unknown }).message) : String(error ?? "");
  const code = error && typeof error === "object" && "code" in error ? String((error as { code?: unknown }).code) : "";
  return ["42P01", "42703", "PGRST200", "PGRST204", "PGRST205"].includes(code) || /does not exist|schema cache|could not find/i.test(text);
}

async function fetchTransactions(companyId: string | null, isPlatformOwner: boolean): Promise<TransactionRow[]> {
  const fullSelect = "id, company_id, website_id, article_id, job_id, user_id, delta, reason, idempotency_key, metadata, balance_before, balance_after, feature_key, action, credit_type, related_entity_type, related_entity_id, status, error_details, created_at";
  const fallbackSelect = "id, company_id, website_id, article_id, job_id, user_id, delta, reason, idempotency_key, metadata, balance_after, created_at";
  const query = (select: string) => scopeQuery(
    db.from("credit_transactions").select(select).order("created_at", { ascending: false }).limit(1500),
    companyId,
    isPlatformOwner,
  );

  const full = await query(fullSelect);
  if (!full.error) return (full.data ?? []) as TransactionRow[];
  if (!isMissingSchemaError(full.error)) throw full.error;

  const fallback = await query(fallbackSelect);
  if (fallback.error) throw fallback.error;
  return ((fallback.data ?? []) as Array<Partial<TransactionRow>>).map((tx) => ({
    id: tx.id ?? "",
    company_id: tx.company_id ?? "",
    website_id: tx.website_id ?? null,
    article_id: tx.article_id ?? null,
    job_id: tx.job_id ?? null,
    user_id: tx.user_id ?? null,
    delta: Number(tx.delta ?? 0),
    reason: tx.reason ?? "unknown",
    idempotency_key: tx.idempotency_key ?? null,
    metadata: tx.metadata ?? null,
    balance_before: null,
    balance_after: Number(tx.balance_after ?? 0),
    feature_key: String(tx.metadata?.feature_key ?? tx.metadata?.task_type ?? tx.reason ?? "unknown"),
    action: String(tx.metadata?.action ?? tx.reason ?? "unknown"),
    credit_type: String(tx.metadata?.credit_type ?? "ai"),
    related_entity_type: tx.article_id ? "article" : tx.job_id ? "autopublish_job" : tx.website_id ? "website" : null,
    related_entity_id: tx.article_id ?? tx.job_id ?? tx.website_id ?? null,
    status: "success",
    error_details: null,
    created_at: tx.created_at ?? new Date(0).toISOString(),
  }));
}

async function fetchFeatureSettings(companyId: string | null, isPlatformOwner: boolean): Promise<FeatureSettingRow[]> {
  const result = await scopeQuery(db.from("feature_credit_settings").select("*").order("feature_key"), companyId, isPlatformOwner);
  if (!result.error) return (result.data ?? []) as FeatureSettingRow[];
  if (isMissingSchemaError(result.error)) return [];
  throw result.error;
}

async function fetchDashboard(companyId: string | null, isPlatformOwner: boolean): Promise<DashboardData> {
  const usageQuery = scopeQuery(
    db
      .from("usage_events")
      .select("id, company_id, website_id, article_id, task_type, model_key, credits_charged, status, created_at")
      .order("created_at", { ascending: false })
      .limit(5000),
    companyId,
    isPlatformOwner,
  );
  const walletQuery = scopeQuery(db.from("credit_wallets").select("company_id, balance, lifetime_granted, lifetime_consumed, updated_at"), companyId, isPlatformOwner);
  const subscriptionQuery = scopeQuery(
    db.from("user_subscriptions").select("company_id, package_id, status, current_period_end, created_at").in("status", ["active", "trialing"]).order("created_at", { ascending: false }),
    companyId,
    isPlatformOwner,
  );
  const websitesQuery = scopeQuery(db.from("websites").select("id, company_id, name, domain").order("name"), companyId, isPlatformOwner);
  const articlesQuery = scopeQuery(db.from("articles").select("id, company_id, website_id, title").order("created_at", { ascending: false }).limit(2000), companyId, isPlatformOwner);
  const companiesQuery = isPlatformOwner
    ? db.from("companies").select("id, name, slug").order("name")
    : db.from("companies").select("id, name, slug").eq("id", companyId);

  const [
    companies,
    wallets,
    subscriptions,
    packageFeatures,
    usage,
    models,
    websites,
    articles,
    profiles,
  ] = await Promise.all([
    companiesQuery,
    walletQuery,
    subscriptionQuery,
    db.from("package_features").select("package_id, feature_value").eq("feature_key", "monthly_credits"),
    usageQuery,
    db.from("ai_models").select("task_type, credits_cost, label").eq("active", true),
    websitesQuery,
    articlesQuery,
    db.from("profiles").select("id, full_name").limit(2000),
  ]);

  for (const result of [companies, wallets, subscriptions, packageFeatures, usage, models, websites, articles, profiles]) {
    if (result.error) throw result.error;
  }

  const [transactions, settings] = await Promise.all([
    fetchTransactions(companyId, isPlatformOwner),
    fetchFeatureSettings(companyId, isPlatformOwner),
  ]);

  return {
    companies: companies.data ?? [],
    wallets: wallets.data ?? [],
    subscriptions: subscriptions.data ?? [],
    packageFeatures: packageFeatures.data ?? [],
    usage: usage.data ?? [],
    transactions,
    settings,
    models: models.data ?? [],
    websites: websites.data ?? [],
    articles: articles.data ?? [],
    profiles: profiles.data ?? [],
  } as DashboardData;
}

function exportCsv(filename: string, rows: Array<Record<string, unknown>>) {
  const headers = Object.keys(rows[0] ?? { empty: "" });
  const escape = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const csv = [headers.join(","), ...rows.map((row) => headers.map((header) => escape(row[header])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function exportPdf(rows: Array<Record<string, unknown>>) {
  const win = window.open("", "_blank", "width=1100,height=800");
  if (!win) return;
  const headers = Object.keys(rows[0] ?? {});
  win.document.write(`
    <html><head><title>Delapresse credit logs</title>
    <style>body{font-family:Arial,sans-serif;padding:24px}table{border-collapse:collapse;width:100%;font-size:11px}th,td{border:1px solid #ddd;padding:6px;text-align:left}th{background:#f3f4f6}</style>
    </head><body><h1>Delapresse credit logs</h1><table><thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead><tbody>
    ${rows.map((row) => `<tr>${headers.map((h) => `<td>${String(row[h] ?? "")}</td>`).join("")}</tr>`).join("")}
    </tbody></table></body></html>
  `);
  win.document.close();
  win.focus();
  win.print();
}

export default function BillingCreditsPage() {
  const { currentCompanyId, currentCompany, isPlatformOwner, role, isLoading: companyLoading } = useCompany();
  const canViewBilling = isPlatformOwner || role === "owner" || role === "admin";
  const trialMode = useTrialMode();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [companyFilter, setCompanyFilter] = useState("all");
  const [websiteFilter, setWebsiteFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [featureFilter, setFeatureFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [creditTypeFilter, setCreditTypeFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");

  const dashboard = useQuery({
    queryKey: ["billing-credits-dashboard", currentCompanyId, isPlatformOwner],
    queryFn: () => fetchDashboard(currentCompanyId, isPlatformOwner),
    enabled: canViewBilling && (isPlatformOwner || !!currentCompanyId),
    refetchInterval: 30_000,
  });
  const packs = useQuery({ queryKey: ["credit-packs"], queryFn: getCreditPacks });

  const canManage = canViewBilling;
  const controlsNeedSingleCompany = isPlatformOwner && companyFilter === "all";
  const data = dashboard.data;
  const monthStart = useMemo(() => startOfMonth(), []);
  const selectedCompanyIds = useMemo(() => {
    if (isPlatformOwner && companyFilter !== "all") return [companyFilter];
    if (!isPlatformOwner && currentCompanyId) return [currentCompanyId];
    return data?.companies.map((company) => company.id) ?? [];
  }, [companyFilter, currentCompanyId, data?.companies, isPlatformOwner]);

  const maps = useMemo(() => {
    const companies = new Map((data?.companies ?? []).map((row) => [row.id, row]));
    const websites = new Map((data?.websites ?? []).map((row) => [row.id, row]));
    const articles = new Map((data?.articles ?? []).map((row) => [row.id, row]));
    const profiles = new Map((data?.profiles ?? []).map((row) => [row.id, row]));
    return { companies, websites, articles, profiles };
  }, [data]);

  const activeSubscriptions = useMemo(() => {
    const byCompany = new Map<string, SubscriptionRow>();
    for (const sub of data?.subscriptions ?? []) {
      if (!sub.company_id || byCompany.has(sub.company_id)) continue;
      byCompany.set(sub.company_id, sub);
    }
    return byCompany;
  }, [data?.subscriptions]);

  const allowanceByPackage = useMemo(() => {
    const map = new Map<string, number>();
    for (const feature of data?.packageFeatures ?? []) {
      const value = typeof feature.feature_value === "number" ? feature.feature_value : Number(feature.feature_value?.amount ?? 0);
      map.set(feature.package_id, value);
    }
    return map;
  }, [data?.packageFeatures]);

  const scopedTransactions = useMemo(() => {
    const selected = new Set(selectedCompanyIds);
    return (data?.transactions ?? []).filter((tx) => selected.has(tx.company_id));
  }, [data?.transactions, selectedCompanyIds]);

  const filteredTransactions = useMemo(() => {
    const q = search.toLowerCase();
    return scopedTransactions.filter((tx) => {
      const websiteId = tx.website_id ?? (tx.metadata?.website_id as string | undefined) ?? "";
      const feature = featureFromTransaction(tx);
      const creditType = tx.credit_type ?? "ai";
      const created = new Date(tx.created_at).getTime();
      const haystack = [
        maps.companies.get(tx.company_id)?.name,
        maps.websites.get(websiteId)?.name,
        maps.articles.get(tx.article_id ?? "")?.title,
        maps.profiles.get(tx.user_id ?? "")?.full_name,
        feature,
        tx.action,
        tx.reason,
        tx.idempotency_key,
        tx.error_details,
      ].join(" ").toLowerCase();
      return (
        (companyFilter === "all" || tx.company_id === companyFilter) &&
        (websiteFilter === "all" || websiteId === websiteFilter) &&
        (userFilter === "all" || tx.user_id === userFilter) &&
        (featureFilter === "all" || feature === featureFilter) &&
        (statusFilter === "all" || tx.status === statusFilter) &&
        (creditTypeFilter === "all" || creditType === creditTypeFilter) &&
        (!fromDate || created >= new Date(fromDate).getTime()) &&
        (!toDate || created <= new Date(toDate).getTime() + 86_400_000) &&
        (!q || haystack.includes(q))
      );
    });
  }, [companyFilter, creditTypeFilter, featureFilter, fromDate, maps, scopedTransactions, search, statusFilter, toDate, userFilter, websiteFilter]);

  const overview = useMemo(() => {
    let balance = 0;
    let lifetimeConsumed = 0;
    let lifetimeGranted = 0;
    let monthlyIncluded = 0;
    let nextReset: string | null = null;
    for (const id of selectedCompanyIds) {
      const wallet = data?.wallets.find((row) => row.company_id === id);
      balance += Number(wallet?.balance ?? 0);
      lifetimeConsumed += Number(wallet?.lifetime_consumed ?? 0);
      lifetimeGranted += Number(wallet?.lifetime_granted ?? 0);
      const sub = activeSubscriptions.get(id);
      if (sub?.package_id) monthlyIncluded += allowanceByPackage.get(sub.package_id) ?? 0;
      if (sub?.current_period_end && (!nextReset || new Date(sub.current_period_end) < new Date(nextReset))) nextReset = sub.current_period_end;
    }
    const usedThisMonth = scopedTransactions
      .filter((tx) => tx.delta < 0 && tx.status === "success" && new Date(tx.created_at) >= monthStart)
      .reduce((sum, tx) => sum + Math.abs(tx.delta), 0);
    const remainingIncluded = Math.max(monthlyIncluded - usedThisMonth, 0);
    return { balance, lifetimeConsumed, lifetimeGranted, monthlyIncluded, usedThisMonth, remainingIncluded, nextReset };
  }, [activeSubscriptions, allowanceByPackage, data?.wallets, monthStart, scopedTransactions, selectedCompanyIds]);

  const modelCosts = useMemo(() => {
    const map = new Map<string, number>();
    for (const model of data?.models ?? []) {
      const current = map.get(model.task_type);
      if (current === undefined || Number(model.credits_cost) < current) map.set(model.task_type, Number(model.credits_cost));
    }
    return map;
  }, [data?.models]);

  const featureRows = useMemo(() => {
    const settingMap = new Map((data?.settings ?? []).filter((s) => selectedCompanyIds.includes(s.company_id)).map((s) => [s.feature_key, s]));
    const featureKeys = new Set([...FEATURE_CATALOG.map((f) => f.key), ...scopedTransactions.map(featureFromTransaction)]);
    return Array.from(featureKeys).map((key) => {
      const catalog = FEATURE_CATALOG.find((item) => item.key === key);
      const setting = settingMap.get(key);
      const task = catalog?.task ?? key;
      const monthTx = scopedTransactions.filter((tx) => featureFromTransaction(tx) === key && tx.delta < 0 && tx.status === "success" && new Date(tx.created_at) >= monthStart);
      const totalCredits = monthTx.reduce((sum, tx) => sum + Math.abs(tx.delta), 0);
      const last = monthTx[0] ?? scopedTransactions.find((tx) => featureFromTransaction(tx) === key);
      const related = last?.article_id ? maps.articles.get(last.article_id)?.title : last?.website_id ? maps.websites.get(last.website_id)?.name : last?.job_id ? last.job_id : null;
      return {
        key,
        name: setting?.feature_name ?? catalog?.name ?? key.replace(/_/g, " "),
        description: setting?.description ?? catalog?.description ?? "Discovered from credit ledger activity.",
        cost: catalog?.task ? modelCosts.get(catalog.task) ?? 0 : 0,
        used: monthTx.length,
        totalCredits,
        lastUsed: last?.created_at ?? null,
        related,
        setting,
      };
    }).sort((a, b) => b.totalCredits - a.totalCredits || a.name.localeCompare(b.name));
  }, [data?.settings, maps.articles, maps.websites, modelCosts, monthStart, scopedTransactions, selectedCompanyIds]);

  const trend = useMemo(() => {
    const map = new Map<string, number>();
    for (const tx of scopedTransactions) {
      if (tx.delta >= 0 || tx.status !== "success") continue;
      const key = monthKey(tx.created_at);
      map.set(key, (map.get(key) ?? 0) + Math.abs(tx.delta));
    }
    return Array.from(map.entries()).reverse().slice(-6).map(([month, credits]) => ({ month, credits }));
  }, [scopedTransactions]);

  const usageByFeature = featureRows.filter((row) => row.totalCredits > 0).map((row) => ({ name: row.name, value: row.totalCredits })).slice(0, 8);
  const topUsers = useMemo(() => {
    const map = new Map<string, number>();
    for (const tx of scopedTransactions) {
      if (tx.delta >= 0 || tx.status !== "success" || !tx.user_id || new Date(tx.created_at) < monthStart) continue;
      map.set(tx.user_id, (map.get(tx.user_id) ?? 0) + Math.abs(tx.delta));
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([id, credits]) => ({ name: maps.profiles.get(id)?.full_name || id.slice(0, 8), credits }));
  }, [maps.profiles, monthStart, scopedTransactions]);
  const topWebsites = useMemo(() => {
    const map = new Map<string, number>();
    for (const tx of scopedTransactions) {
      if (tx.delta >= 0 || tx.status !== "success" || !tx.website_id || new Date(tx.created_at) < monthStart) continue;
      map.set(tx.website_id, (map.get(tx.website_id) ?? 0) + Math.abs(tx.delta));
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([id, credits]) => ({ name: maps.websites.get(id)?.name || id.slice(0, 8), credits }));
  }, [maps.websites, monthStart, scopedTransactions]);

  const updateSetting = useMutation({
    mutationFn: async ({ setting, patch }: { setting: FeatureSettingRow; patch: Partial<FeatureSettingRow> }) => {
      if (trialMode.isReadOnly) throw new Error(TRIAL_READ_ONLY_MESSAGE);
      const { error } = await db
        .from("feature_credit_settings")
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq("id", setting.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["billing-credits-dashboard"] }),
    onError: (err) => toast({ variant: "destructive", description: err instanceof Error ? err.message : String(err) }),
  });

  const reverseTransaction = useMutation({
    mutationFn: async (tx: TransactionRow) => {
      if (trialMode.isReadOnly) throw new Error(TRIAL_READ_ONLY_MESSAGE);
      const { data: response, error } = await db.functions.invoke("credit-management", {
        body: { action: "reverse_transaction", transaction_id: tx.id, reason: "manual reversal from credits dashboard" },
      });
      if (error) throw error;
      if (response?.error) throw new Error(response.error);
    },
    onSuccess: () => {
      toast({ description: "Credit transaction reversed." });
      qc.invalidateQueries({ queryKey: ["billing-credits-dashboard"] });
    },
    onError: (err) => toast({ variant: "destructive", description: err instanceof Error ? err.message : String(err) }),
  });

  if (!isPlatformOwner && !currentCompanyId && companyLoading) {
    return <div className="container mx-auto flex items-center gap-2 py-8 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading company context...</div>;
  }

  if (dashboard.isLoading && !dashboard.data) {
    return <div className="container mx-auto flex items-center gap-2 py-8 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading credit center...</div>;
  }

  if (!isPlatformOwner && !currentCompanyId) {
    return <div className="container mx-auto py-8 text-muted-foreground">No active company.</div>;
  }

  if (!canViewBilling) {
    return <div className="container mx-auto py-8 text-muted-foreground">Credits and billing controls are available only to company owners and admins.</div>;
  }

  const lowCredit = overview.balance <= 0 || (overview.monthlyIncluded > 0 && overview.balance <= Math.max(50, overview.monthlyIncluded * 0.1));
  const csvRows = filteredTransactions.map((tx) => ({
    date_time: tx.created_at,
    company: maps.companies.get(tx.company_id)?.name ?? tx.company_id,
    user: maps.profiles.get(tx.user_id ?? "")?.full_name ?? tx.user_id ?? "system",
    feature: featureFromTransaction(tx),
    action: tx.action ?? tx.reason,
    credits_debited: tx.delta < 0 ? Math.abs(tx.delta) : 0,
    balance_before: tx.balance_before ?? tx.balance_after - tx.delta,
    balance_after: tx.balance_after,
    related_entity_type: tx.related_entity_type ?? "",
    related_entity_id: tx.related_entity_id ?? tx.article_id ?? tx.website_id ?? tx.job_id ?? "",
    status: tx.status,
    error_details: tx.error_details ?? "",
  }));

  return (
    <div className="container mx-auto max-w-7xl space-y-6 py-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Credit analysis and management</h1>
          <p className="text-sm text-muted-foreground">
            {isPlatformOwner ? "Platform-wide credit balance, feature controls, limits, and audit logs." : `${currentCompany?.name ?? "Company"} credit balance, usage, and controls.`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => dashboard.refetch()}><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
          <Button variant="outline" size="sm" onClick={() => exportCsv("delapresse-credit-logs.csv", csvRows)} disabled={!csvRows.length}><Download className="mr-2 h-4 w-4" />CSV</Button>
          <Button variant="outline" size="sm" onClick={() => exportPdf(csvRows)} disabled={!csvRows.length}><FileText className="mr-2 h-4 w-4" />PDF</Button>
        </div>
      </div>

      {dashboard.error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Credit center query failed</AlertTitle>
          <AlertDescription>
            {dashboard.error instanceof Error ? dashboard.error.message : String(dashboard.error)}
          </AlertDescription>
        </Alert>
      )}

      {lowCredit && (
        <Alert variant={overview.balance <= 0 ? "destructive" : "default"}>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{overview.balance <= 0 ? "Credits exhausted" : "Low credit balance"}</AlertTitle>
          <AlertDescription>
            Actions that use AI credits will be blocked when the wallet cannot cover the model cost. Add credits or lower feature limits before scheduled jobs run.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
        <MetricCard icon={CreditCard} label="Current balance" value={overview.balance.toLocaleString()} detail="Wallet credits available now" />
        <MetricCard icon={Sparkles} label="Monthly included" value={overview.monthlyIncluded.toLocaleString()} detail="Active plan allowance" />
        <MetricCard icon={BarChart3} label="Used this month" value={overview.usedThisMonth.toLocaleString()} detail="Successful debits only" />
        <MetricCard icon={CheckCircle2} label="Remaining included" value={overview.remainingIncluded.toLocaleString()} detail="Allowance minus monthly use" />
        <MetricCard icon={TrendingUp} label="Lifetime consumed" value={overview.lifetimeConsumed.toLocaleString()} detail={`${overview.lifetimeGranted.toLocaleString()} granted`} />
        <MetricCard icon={RefreshCw} label="Next reset" value={overview.nextReset ? new Date(overview.nextReset).toLocaleDateString() : "Not set"} detail="Plan renewal date" />
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader><CardTitle className="text-base">Monthly usage trend</CardTitle></CardHeader>
          <CardContent className="h-64">
            {trend.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Area dataKey="credits" stroke="#2563eb" fill="#2563eb" fillOpacity={0.15} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <EmptyState text="No monthly credit activity yet." />}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Usage by feature</CardTitle></CardHeader>
          <CardContent className="h-64">
            {usageByFeature.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={usageByFeature} dataKey="value" nameKey="name" innerRadius={48} outerRadius={82} paddingAngle={2}>
                    {usageByFeature.map((_, index) => <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <EmptyState text="No feature usage this month." />}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <TopChart title="Top credit-consuming users" data={topUsers} />
        <TopChart title="Top credit-consuming websites" data={topWebsites} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><SlidersHorizontal className="h-4 w-4" />Credit-consuming features</CardTitle>
          <CardDescription>Costs come from the cheapest active model for the underlying task. Actual debits are recorded in the ledger with idempotency keys.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Feature</TableHead>
                <TableHead>Cost/action</TableHead>
                <TableHead className="text-right">Used</TableHead>
                <TableHead className="text-right">Credits</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Related item</TableHead>
                <TableHead>Controls</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {featureRows.map((row) => (
                <TableRow key={row.key}>
                  <TableCell className="max-w-[320px]">
                    <div className="font-medium">{row.name}</div>
                    <div className="text-xs text-muted-foreground">{row.description}</div>
                  </TableCell>
                  <TableCell>{row.cost ? `${row.cost} cr` : "0 or usage-based"}</TableCell>
                  <TableCell className="text-right">{row.used}</TableCell>
                  <TableCell className="text-right font-semibold">{row.totalCredits}</TableCell>
                  <TableCell className="text-xs">{formatDateTime(row.lastUsed)}</TableCell>
                  <TableCell className="max-w-[220px] truncate text-xs text-muted-foreground">{row.related ?? "None recorded"}</TableCell>
                  <TableCell>
                    {row.setting ? (
                      <div className="space-y-2">
                        {controlsNeedSingleCompany && <Badge variant="outline">Select one company to edit</Badge>}
                        <FeatureControls setting={row.setting} disabled={!canManage || updateSetting.isPending || controlsNeedSingleCompany} onChange={(patch) => updateSetting.mutate({ setting: row.setting!, patch })} />
                      </div>
                    ) : (
                      <Badge variant="outline">Detected from logs</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Search className="h-4 w-4" />Credit logs</CardTitle>
          <CardDescription>Ledger entries include company, user, feature, action, balances, status, related entity, and error/reversal details.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
            {isPlatformOwner && (
              <Select value={companyFilter} onValueChange={setCompanyFilter}>
                <SelectTrigger><SelectValue placeholder="Company" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All companies</SelectItem>{(data?.companies ?? []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Select value={websiteFilter} onValueChange={setWebsiteFilter}>
              <SelectTrigger><SelectValue placeholder="Website" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All websites</SelectItem>{(data?.websites ?? []).filter((w) => selectedCompanyIds.includes(w.company_id ?? "")).map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={featureFilter} onValueChange={setFeatureFilter}>
              <SelectTrigger><SelectValue placeholder="Feature" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All features</SelectItem>{featureRows.map((f) => <SelectItem key={f.key} value={f.key}>{f.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="success">Success</SelectItem><SelectItem value="failed">Failed</SelectItem><SelectItem value="reversed">Reversed</SelectItem></SelectContent>
            </Select>
            <Select value={creditTypeFilter} onValueChange={setCreditTypeFilter}>
              <SelectTrigger><SelectValue placeholder="Credit type" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All credit types</SelectItem><SelectItem value="ai">AI</SelectItem><SelectItem value="media">Media</SelectItem><SelectItem value="grant">Grant</SelectItem></SelectContent>
            </Select>
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger><SelectValue placeholder="User" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All users</SelectItem>{(data?.profiles ?? []).map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name || p.id.slice(0, 8)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search action, user, entity, idempotency key..." />
          </div>

          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date/time</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Feature/action</TableHead>
                  <TableHead className="text-right">Debited</TableHead>
                  <TableHead className="text-right">Before</TableHead>
                  <TableHead className="text-right">After</TableHead>
                  <TableHead>Related</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Idempotency</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.length === 0 ? (
                  <TableRow><TableCell colSpan={11}><EmptyState text="No credit transactions match these filters." /></TableCell></TableRow>
                ) : filteredTransactions.slice(0, 250).map((tx) => {
                  const website = maps.websites.get(tx.website_id ?? "");
                  const article = maps.articles.get(tx.article_id ?? "");
                  const userName = maps.profiles.get(tx.user_id ?? "")?.full_name ?? tx.user_id?.slice(0, 8) ?? "system";
                  return (
                    <TableRow key={tx.id}>
                      <TableCell className="whitespace-nowrap text-xs">{formatDateTime(tx.created_at)}</TableCell>
                      <TableCell className="text-xs">{maps.companies.get(tx.company_id)?.name ?? tx.company_id.slice(0, 8)}</TableCell>
                      <TableCell className="text-xs">{userName}</TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">{featureFromTransaction(tx)}</div>
                        <div className="text-xs text-muted-foreground">{tx.action ?? tx.reason}</div>
                      </TableCell>
                      <TableCell className="text-right font-semibold">{tx.delta < 0 ? Math.abs(tx.delta) : 0}</TableCell>
                      <TableCell className="text-right">{tx.balance_before ?? tx.balance_after - tx.delta}</TableCell>
                      <TableCell className="text-right">{tx.balance_after}</TableCell>
                      <TableCell className="max-w-[220px] text-xs">
                        <div className="truncate">{article?.title ?? website?.name ?? tx.related_entity_type ?? "None"}</div>
                        <div className="truncate text-muted-foreground">{tx.related_entity_id ?? tx.article_id ?? tx.website_id ?? tx.job_id ?? ""}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={tx.status === "success" ? "secondary" : tx.status === "reversed" ? "outline" : "destructive"}>{tx.status}</Badge>
                        {tx.error_details && <div className="mt-1 max-w-[180px] truncate text-xs text-destructive">{tx.error_details}</div>}
                      </TableCell>
                      <TableCell className="max-w-[180px] truncate text-xs">{tx.idempotency_key}</TableCell>
                      <TableCell className="text-right">
                        {canManage && tx.delta < 0 && tx.status === "success" ? (
                          <Button variant="ghost" size="sm" onClick={() => reverseTransaction.mutate(tx)} disabled={reverseTransaction.isPending || trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Credit safety status</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <SafetyLine ok label="All AI provider calls route through the shared credit reservation helper." />
            <SafetyLine ok label="Idempotency keys are required and duplicate debits return the original transaction." />
            <SafetyLine ok label="Failed provider calls are refunded through the credit reversal path." />
            <SafetyLine ok label="Production test/debug/concurrency debit keys are blocked by SQL and Edge Function guards." />
            <SafetyLine ok label="Company RLS protects wallets, settings, summaries, and transaction reads." />
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Top up credits</CardTitle><CardDescription>One-time packs add to the company wallet.</CardDescription></CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            {(packs.data ?? []).slice(0, 4).map((pack) => (
              <div key={pack.id} className="rounded-md border p-3">
                <div className="flex items-center justify-between"><span className="text-xl font-bold">{pack.credits.toLocaleString()}</span><Badge variant="secondary">credits</Badge></div>
                <div className="mt-1 text-sm text-muted-foreground">{money(pack.price_cents / 100, pack.currency)}</div>
                <Button asChild={!trialMode.isReadOnly} className="mt-3 w-full" size="sm" disabled={trialMode.isReadOnly} title={trialMode.isReadOnly ? TRIAL_READ_ONLY_MESSAGE : undefined}>
                  {trialMode.isReadOnly ? <span>Buy credits</span> : <Link to={`/checkout/credits/${pack.sku}`}>Buy credits</Link>}
                </Button>
              </div>
            ))}
            {!packs.data?.length && <EmptyState text="No credit packs are currently active." />}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, detail }: { icon: React.ElementType; label: string; value: string; detail: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10"><Icon className="h-4 w-4 text-primary" /></div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="truncate text-2xl font-bold">{value}</p>
          </div>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{detail}</p>
      </CardContent>
    </Card>
  );
}

function FeatureControls({ setting, disabled, onChange }: { setting: FeatureSettingRow; disabled: boolean; onChange: (patch: Partial<FeatureSettingRow>) => void }) {
  return (
    <div className="grid min-w-[360px] gap-2 text-xs">
      <div className="grid grid-cols-3 gap-2">
        <ToggleLine label="Enabled" checked={setting.enabled} disabled={disabled} onChange={(enabled) => onChange({ enabled })} />
        <ToggleLine label="Confirm" checked={setting.require_confirmation} disabled={disabled} onChange={(require_confirmation) => onChange({ require_confirmation })} />
        <ToggleLine label="Override" checked={setting.allow_admin_override} disabled={disabled} onChange={(allow_admin_override) => onChange({ allow_admin_override })} />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <label className="space-y-1">
          <span className="text-muted-foreground">Monthly limit</span>
          <Input className="h-8" type="number" min={0} disabled={disabled} value={numericInputValue(setting.monthly_credit_limit)} onChange={(e) => onChange({ monthly_credit_limit: e.target.value === "" ? null : Number(e.target.value) })} />
        </label>
        <label className="space-y-1">
          <span className="text-muted-foreground">Daily limit</span>
          <Input className="h-8" type="number" min={0} disabled={disabled} value={numericInputValue(setting.daily_credit_limit)} onChange={(e) => onChange({ daily_credit_limit: e.target.value === "" ? null : Number(e.target.value) })} />
        </label>
        <div className="flex items-end">
          <label className="flex items-center gap-2 pb-2">
            <Checkbox checked={setting.block_when_limit_reached} disabled={disabled} onCheckedChange={(v) => onChange({ block_when_limit_reached: Boolean(v) })} />
            <span>Block at limit</span>
          </label>
        </div>
      </div>
      {!setting.enabled && <div className="flex items-center gap-1 text-destructive"><ShieldAlert className="h-3 w-3" />Feature spending disabled</div>}
    </div>
  );
}

function ToggleLine({ label, checked, disabled, onChange }: { label: string; checked: boolean; disabled: boolean; onChange: (value: boolean) => void }) {
  return <Label className="flex items-center justify-between gap-2 rounded-md border px-2 py-1.5"><span>{label}</span><Switch checked={checked} disabled={disabled} onCheckedChange={onChange} /></Label>;
}

function TopChart({ title, data }: { title: string; data: Array<{ name: string; credits: number }> }) {
  const max = Math.max(...data.map((row) => row.credits), 1);
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        {data.length ? (
          <div className="space-y-3">
            {data.map((row) => (
              <div key={row.name} className="space-y-1">
                <div className="flex justify-between text-xs"><span className="truncate">{row.name}</span><span>{row.credits} cr</span></div>
                <Progress value={(row.credits / max) * 100} />
              </div>
            ))}
          </div>
        ) : <EmptyState text="No usage this month." />}
      </CardContent>
    </Card>
  );
}

function SafetyLine({ ok, label }: { ok: boolean; label: string }) {
  return <div className="flex items-start gap-2">{ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-success" /> : <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive" />}<span>{label}</span></div>;
}

function EmptyState({ text }: { text: string }) {
  return <div className="flex min-h-24 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">{text}</div>;
}
